// src/components/kb/KbReplaceForm.tsx
//
// Replace-content overlay for a KB document (Story 2.2). A file picker plus a
// confirm step whose copy names the consequence: a replacement is ADDITIVE —
// Yak answers from the new content, but every prior version stays readable and
// stays pinned to the answers it already supported ("Nothing disappears"). It
// delegates to replaceContent (→ kb-ingest replace mode → yak_kb_add_version),
// so ingest takes seconds and the submit button shows a pending "Processing…"
// state; failures render an inline retryable banner. No optimistic updates — the
// new version appears only after the parent refetches. Esc closes; focus
// returns to the trigger on close (stable fallback when the trigger unmounted).

import React, { useEffect, useId, useRef, useState } from 'react'
import { Upload, X } from 'lucide-react'
import { Button } from '../ui/Button'

// Mirror the edge function's bounds so we fail fast client-side before a
// pointless upload (the edge re-validates — it is the real boundary).
const ACCEPT = '.pdf,.md,.txt'
const MAX_FILE_BYTES = 10 * 1024 * 1024

interface KbReplaceFormProps {
  documentTitle: string
  onReplace: (file: File) => Promise<void>
  onClose: () => void
}

export function KbReplaceForm({
  documentTitle,
  onReplace,
  onClose,
}: KbReplaceFormProps) {
  const [file, setFile] = useState<File | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fileErrorId = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    fileInputRef.current?.focus()
  }, [])

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const picked = e.target.files?.[0] ?? null
    if (!picked) {
      setFile(null)
      return
    }
    if (picked.size > MAX_FILE_BYTES) {
      setFile(null)
      setError('File is too large. The limit is 10 MB.')
      return
    }
    setFile(picked)
  }

  const canSubmit = !!file && !pending

  const submit = async () => {
    if (!canSubmit || !file) return
    setPending(true)
    setError(null)
    try {
      await onReplace(file)
      onClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "The new version couldn't be processed — the previous version is still active. Try again."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape' && !pending) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kb-replace-title"
        className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6"
      >
        <div className="flex items-start justify-between mb-1">
          <h3
            id="kb-replace-title"
            className="text-lg font-semibold text-gray-900"
          >
            Replace content
          </h3>
          <button
            type="button"
            onClick={() => !pending && onClose()}
            disabled={pending}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Yak will answer from the new content for{' '}
          <span className="font-medium text-gray-900">{documentTitle}</span>.
          Previous versions remain readable and stay pinned to past answers.
          PDF, Markdown or plain text, up to 10 MB.
        </p>

        {error && (
          <div
            role="alert"
            className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="kb-replace-file" className="form-label">
            New content file
          </label>
          <input
            ref={fileInputRef}
            id="kb-replace-file"
            type="file"
            accept={ACCEPT}
            disabled={pending}
            aria-describedby={fileErrorId}
            onChange={onFileChange}
            className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:bg-white hover:file:bg-gray-50 disabled:opacity-50"
          />
          <span id={fileErrorId} className="form-help">
            Accepted types: PDF, Markdown, plain text. Maximum 10 MB.
          </span>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={() => onClose()} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending} disabled={!canSubmit}>
            {!pending && <Upload className="w-4 h-4 mr-1" />}
            {pending ? 'Processing…' : 'Create new version'}
          </Button>
        </div>
      </div>
    </div>
  )
}
